import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * RiskTab
 *
 * Handles Risk Assessment section for MH triage assessments
 * Maps to Risk.Triage table fields
 */

const RiskTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [riskTowardsSelf, setRiskTowardsSelf] = useState(null);
  const [currentThoughtsSelfHarm, setCurrentThoughtsSelfHarm] = useState('');
  const [suicidalScale, setSuicidalScale] = useState(null);
  const [suicidalPlansToday, setSuicidalPlansToday] = useState(null);
  const [suicidalPlansTodayInformation, setSuicidalPlansTodayInformation] = useState('');
  const [previousAttemptSelfHarm, setPreviousAttemptSelfHarm] = useState(null);
  const [previousAttemptSelfHarmInformation, setPreviousAttemptSelfHarmInformation] = useState('');
  const [protectiveFactor, setProtectiveFactor] = useState('');
  const [safetyPlan, setSafetyPlan] = useState('');
  const [riskFromOthers, setRiskFromOthers] = useState(null);
  const [riskFromOthersInformation, setRiskFromOthersInformation] = useState('');
  const [riskInformation, setRiskInformation] = useState('');
  const [clientRiskSummary, setClientRiskSummary] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadRiskData();
  }, [reportId]);

  const loadRiskData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-risk`);

      if (response.ok) {
        const data = await response.json();
        setRiskTowardsSelf(data.riskTowardsSelf);
        setCurrentThoughtsSelfHarm(data.currentThoughtsSelfHarm || '');
        setSuicidalScale(data.suicidalScale);
        setSuicidalPlansToday(data.suicidalPlansToday);
        setSuicidalPlansTodayInformation(data.suicidalPlansTodayInformation || '');
        setPreviousAttemptSelfHarm(data.previousAttemptSelfHarm);
        setPreviousAttemptSelfHarmInformation(data.previousAttemptSelfHarmInformation || '');
        setProtectiveFactor(data.protectiveFactor || '');
        setSafetyPlan(data.safetyPlan || '');
        setRiskFromOthers(data.riskFromOthers);
        setRiskFromOthersInformation(data.riskFromOthersInformation || '');
        setRiskInformation(data.riskInformation || '');
        setClientRiskSummary(data.clientRiskSummary || '');

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.riskTowardsSelf !== null) initialSavingStates['RiskTowardsSelf'] = 'saved';
        if (data.currentThoughtsSelfHarm) initialSavingStates['CurrentThoughtsSelfHarm'] = 'saved';
        if (data.suicidalScale !== null) initialSavingStates['SuicidalScale'] = 'saved';
        if (data.suicidalPlansToday !== null) initialSavingStates['SuicidalPlansToday'] = 'saved';
        if (data.suicidalPlansTodayInformation) initialSavingStates['SuicidalPlansTodayInformation'] = 'saved';
        if (data.previousAttemptSelfHarm !== null) initialSavingStates['PreviousAttemptSelfHarm'] = 'saved';
        if (data.previousAttemptSelfHarmInformation) initialSavingStates['PreviousAttemptSelfHarmInformation'] = 'saved';
        if (data.protectiveFactor) initialSavingStates['ProtectiveFactor'] = 'saved';
        if (data.safetyPlan) initialSavingStates['SafetyPlan'] = 'saved';
        if (data.riskFromOthers !== null) initialSavingStates['RiskFromOthers'] = 'saved';
        if (data.riskFromOthersInformation) initialSavingStates['RiskFromOthersInformation'] = 'saved';
        if (data.riskInformation) initialSavingStates['RiskInformation'] = 'saved';
        if (data.clientRiskSummary) initialSavingStates['ClientRiskSummary'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading risk data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-risk/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    // Field value map for checking if field has content
    const fieldValues = {
      'RiskTowardsSelf': riskTowardsSelf,
      'CurrentThoughtsSelfHarm': currentThoughtsSelfHarm,
      'SuicidalScale': suicidalScale,
      'SuicidalPlansToday': suicidalPlansToday,
      'SuicidalPlansTodayInformation': suicidalPlansTodayInformation,
      'PreviousAttemptSelfHarm': previousAttemptSelfHarm,
      'PreviousAttemptSelfHarmInformation': previousAttemptSelfHarmInformation,
      'ProtectiveFactor': protectiveFactor,
      'SafetyPlan': safetyPlan,
      'RiskFromOthers': riskFromOthers,
      'RiskFromOthersInformation': riskFromOthersInformation,
      'RiskInformation': riskInformation,
      'ClientRiskSummary': clientRiskSummary
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleNumericChange = (fieldName, value, setter) => {
    const numericValue = value === '' ? null : parseInt(value, 10);
    setter(numericValue);
    saveField(fieldName, numericValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading risk assessment...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="patient-summary">
        <div><strong>Case ID:</strong> {patientInfo?.caseId}</div>
        <div><strong>Name:</strong> {patientInfo?.name}</div>
        <div><strong>DOB:</strong> {patientInfo?.dateOfBirth}</div>
        <div><strong>Address:</strong> {patientInfo?.address}</div>
      </div>

      <div className="form-section">
        <h3>Risk to Self</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>Do you currently feel that you are at risk to yourself?</strong>
            {getSaveIndicator('RiskTowardsSelf')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="riskTowardsSelf"
                checked={riskTowardsSelf === true}
                onChange={() => handleBooleanChange('RiskTowardsSelf', true, setRiskTowardsSelf)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="riskTowardsSelf"
                checked={riskTowardsSelf === false}
                onChange={() => handleBooleanChange('RiskTowardsSelf', false, setRiskTowardsSelf)}
              />
              No
            </label>
          </div>
        </div>

        {riskTowardsSelf === true && (
          <>
            <div className="form-group">
              <label htmlFor="currentThoughtsSelfHarm">
                What are your current thoughts about self-harm or ending your life?
                {getSaveIndicator('CurrentThoughtsSelfHarm')}
              </label>
              <textarea
                id="currentThoughtsSelfHarm"
                value={currentThoughtsSelfHarm}
                onChange={(e) => handleFieldChange('CurrentThoughtsSelfHarm', e.target.value, setCurrentThoughtsSelfHarm)}
                onBlur={() => handleFieldBlur('CurrentThoughtsSelfHarm', currentThoughtsSelfHarm)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="suicidalScale">
                On a scale of 0-10 (0 being not at all, 10 being definitely) how likely are you to end your life today?
                {getSaveIndicator('SuicidalScale')}
              </label>
              <select
                id="suicidalScale"
                value={suicidalScale === null ? '' : suicidalScale}
                onChange={(e) => handleNumericChange('SuicidalScale', e.target.value, setSuicidalScale)}
                style={{ width: '150px' }}
              >
                <option value="">Select...</option>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            {suicidalScale !== null && suicidalScale > 0 && (
              <div className="risk-alert-banner">
                PLEASE COMPLETE SAFETY PLAN AND RISK ASSESSMENT
              </div>
            )}

            <div className="form-group">
              <label className="inline-label">
                <strong>Have you made plans to end your life?</strong>
                {getSaveIndicator('SuicidalPlansToday')}
              </label>
              <div className="radio-group-inline">
                <label>
                  <input
                    type="radio"
                    name="suicidalPlansToday"
                    checked={suicidalPlansToday === true}
                    onChange={() => handleBooleanChange('SuicidalPlansToday', true, setSuicidalPlansToday)}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="suicidalPlansToday"
                    checked={suicidalPlansToday === false}
                    onChange={() => handleBooleanChange('SuicidalPlansToday', false, setSuicidalPlansToday)}
                  />
                  No
                </label>
              </div>
            </div>

            {suicidalPlansToday === true && (
              <div className="form-group">
                <label htmlFor="suicidalPlansTodayInformation">
                  What plans have you made to end your life today?
                  {getSaveIndicator('SuicidalPlansTodayInformation')}
                </label>
                <textarea
                  id="suicidalPlansTodayInformation"
                  value={suicidalPlansTodayInformation}
                  onChange={(e) => handleFieldChange('SuicidalPlansTodayInformation', e.target.value, setSuicidalPlansTodayInformation)}
                  onBlur={() => handleFieldBlur('SuicidalPlansTodayInformation', suicidalPlansTodayInformation)}
                  rows="4"
                  style={{ width: '100%' }}
                />
              </div>
            )}

          </>
        )}
        <div className="form-group">
            <label className="inline-label">
            <strong>Have you previously attempted suicide or self-harm?</strong>
            {getSaveIndicator('PreviousAttemptSelfHarm')}
            </label>
            <div className="radio-group-inline">
            <label>
                <input
                type="radio"
                name="previousAttemptSelfHarm"
                checked={previousAttemptSelfHarm === true}
                onChange={() => handleBooleanChange('PreviousAttemptSelfHarm', true, setPreviousAttemptSelfHarm)}
                />
                Yes
            </label>
            <label>
                <input
                type="radio"
                name="previousAttemptSelfHarm"
                checked={previousAttemptSelfHarm === false}
                onChange={() => handleBooleanChange('PreviousAttemptSelfHarm', false, setPreviousAttemptSelfHarm)}
                />
                No
            </label>
            </div>
        </div>

        {previousAttemptSelfHarm === true && (
            <div className="form-group">
            <label htmlFor="previousAttemptSelfHarmInformation">
                Previous attempts and/or self-harm information:
                {getSaveIndicator('PreviousAttemptSelfHarmInformation')}
            </label>
            <textarea
                id="previousAttemptSelfHarmInformation"
                value={previousAttemptSelfHarmInformation}
                onChange={(e) => handleFieldChange('PreviousAttemptSelfHarmInformation', e.target.value, setPreviousAttemptSelfHarmInformation)}
                onBlur={() => handleFieldBlur('PreviousAttemptSelfHarmInformation', previousAttemptSelfHarmInformation)}
                rows="4"
                style={{ width: '100%' }}
            />
            </div>
        )}
      </div>

      {riskTowardsSelf === true && (
        <div className="form-section">
          <h3>Protective Factors and Safety Planning</h3>

          <div className="form-group">
            <label htmlFor="protectiveFactor">
              What is your protective factor?
              {getSaveIndicator('ProtectiveFactor')}
            </label>
            <textarea
              id="protectiveFactor"
              value={protectiveFactor}
              onChange={(e) => handleFieldChange('ProtectiveFactor', e.target.value, setProtectiveFactor)}
              onBlur={() => handleFieldBlur('ProtectiveFactor', protectiveFactor)}
              rows="4"
              style={{ width: '100%' }}
              placeholder="e.g., family support, pets, hobbies, future goals"
            />
          </div>

          <div className="form-group">
            <label htmlFor="safetyPlan">
              {patientInfo?.isChildAtReferral ?
              'Detail safety plan and document this has been shared with parent/carer) (Give crisis numbers: GP, A and E, Samaritans (116 123), Kooth (www.kooth.com), Papyrus (0800 068 4141))' :
              'What is your safety plan? (Detail safety plan) (Give crisis numbers: GP, A and E, Samaritans (116 123))'}
              {getSaveIndicator('SafetyPlan')}
            </label>
            <textarea
              id="safetyPlan"
              value={safetyPlan}
              onChange={(e) => handleFieldChange('SafetyPlan', e.target.value, setSafetyPlan)}
              onBlur={() => handleFieldBlur('SafetyPlan', safetyPlan)}
              rows="6"
              style={{ width: '100%' }}
              placeholder="Include: warning signs, coping strategies, people to contact, professional help contacts (GP, A&E, Samaritans 116 123)"
            />
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Risk from Others</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>Do you feel at risk from others?</strong>
            {getSaveIndicator('RiskFromOthers')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="riskFromOthers"
                checked={riskFromOthers === true}
                onChange={() => handleBooleanChange('RiskFromOthers', true, setRiskFromOthers)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="riskFromOthers"
                checked={riskFromOthers === false}
                onChange={() => handleBooleanChange('RiskFromOthers', false, setRiskFromOthers)}
              />
              No
            </label>
          </div>
        </div>

        {riskFromOthers === true && (
          <div className="form-group">
            <label htmlFor="riskFromOthersInformation">
              How do you feel at risk from others, please detail:
              {getSaveIndicator('RiskFromOthersInformation')}
            </label>
            <textarea
              id="riskFromOthersInformation"
              value={riskFromOthersInformation}
              onChange={(e) => handleFieldChange('RiskFromOthersInformation', e.target.value, setRiskFromOthersInformation)}
              onBlur={() => handleFieldBlur('RiskFromOthersInformation', riskFromOthersInformation)}
              rows="4"
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Additional Risk Information</h3>
        <div className="form-group">
          <label htmlFor="riskInformation">
            {
              patientInfo?.isChildAtReferral ?
              'Any other safeguarding or risk information eg eating disorder risk, exploitation risk, safeguarding concerns including family complexity and systemic risk (school/community/social media)':
              'Any other safeguarding or risk information'
            }:
            {getSaveIndicator('RiskInformation')}
          </label>
          <textarea
            id="riskInformation"
            value={riskInformation}
            onChange={(e) => handleFieldChange('RiskInformation', e.target.value, setRiskInformation)}
            onBlur={() => handleFieldBlur('RiskInformation', riskInformation)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="clientRiskSummary">
            Client Risk Summary:
            {getSaveIndicator('ClientRiskSummary')}
          </label>
          <textarea
            id="clientRiskSummary"
            value={clientRiskSummary}
            onChange={(e) => handleFieldChange('ClientRiskSummary', e.target.value, setClientRiskSummary)}
            onBlur={() => handleFieldBlur('ClientRiskSummary', clientRiskSummary)}
            rows="6"
            style={{ width: '100%' }}
            placeholder="Overall summary of client's risk status and actions taken"
          />
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default RiskTab;
